import { useEffect, useRef, RefObject } from 'react';
import * as modelViewer from '@google/model-viewer';
import { Annotation } from '@/types/Annotation.model';

interface UseAnnotationPositionUpdateOptions {
  modelViewerRef: RefObject<modelViewer.ModelViewerElement>;
  activeAnnotation: Annotation | null;
  setAnnotationPosition: (position: { x: number; y: number } | null) => void;
}

/**
 * Keeps the annotation info box anchored to the hotspot screen position.
 * Updates immediately on selection, then tracks camera motion via rAF (no settle delay).
 */
export function useAnnotationPositionUpdate({
  modelViewerRef,
  activeAnnotation,
  setAnnotationPosition,
}: UseAnnotationPositionUpdateOptions) {
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const mv = modelViewerRef.current;
    if (!mv || !activeAnnotation) return;

    const applyPosition = () => {
      const hotspot = mv.querySelector(`[slot="hotspot-${activeAnnotation.id}"]`) as HTMLElement;
      if (!hotspot) return;

      const rect = hotspot.getBoundingClientRect();
      const viewerRect = mv.getBoundingClientRect();
      setAnnotationPosition({
        x: rect.left + rect.width / 2 - viewerRect.left,
        y: rect.top + rect.height / 2 - viewerRect.top,
      });
    };

    const schedulePositionUpdate = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        applyPosition();
      });
    };

    const handleCameraChange = () => {
      schedulePositionUpdate();
    };

    applyPosition();
    requestAnimationFrame(applyPosition);

    mv.addEventListener('camera-change', handleCameraChange);

    return () => {
      mv.removeEventListener('camera-change', handleCameraChange);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [modelViewerRef, activeAnnotation, setAnnotationPosition]);
}
